/**
 * <ReserveHandleDialog /> — "Reserve your handle" flow for anonymous visitors.
 *
 * Flow:
 *   1. User types a candidate handle
 *   2. Debounced `/api/public/username/check` call shows availability inline
 *   3. On "Reserve this handle":
 *      a. Save to sessionStorage.pending_username
 *      b. Fire Google OAuth
 *   4. After OAuth, App.js post-auth effect reads pending_username and PATCHes
 *      /api/profile/username to claim it.
 */

import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Check, Loader2, X } from "lucide-react";

const API = "/api";

export default function ReserveHandleDialog({ open, onOpenChange }) {
  const [value, setValue] = useState("");
  const [state, setState] = useState({ status: "idle", reason: null });
  const timerRef = useRef(null);

  // Debounced availability check
  useEffect(() => {
    clearTimeout(timerRef.current);
    const raw = value.trim().toLowerCase();
    if (!raw) {
      setState({ status: "idle", reason: null });
      return;
    }
    setState({ status: "checking", reason: null });
    timerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `${API}/public/username/check?value=${encodeURIComponent(raw)}`
        );
        const d = await res.json();
        if (!d.valid) {
          setState({ status: "invalid", reason: d.reason || "Invalid" });
        } else if (!d.available) {
          setState({ status: "taken", reason: d.reason || "Already taken" });
        } else {
          setState({ status: "available", reason: null });
        }
      } catch {
        setState({ status: "idle", reason: "Could not check — try again" });
      }
    }, 360);
    return () => clearTimeout(timerRef.current);
  }, [value]);

  const reserveAndSignUp = () => {
    const clean = value.trim().toLowerCase();
    if (state.status !== "available" || !clean) return;
    sessionStorage.setItem("pending_username", clean);
    window.location.href = `${API}/auth/google/login`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reserve your handle</DialogTitle>
          <DialogDescription>
            Your @handle becomes the URL for your public walls and profile.
            Pick one before someone else does.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div
              className="flex items-center rounded-lg border px-3 py-2.5 transition-colors"
              style={{
                borderColor:
                  state.status === "taken" || state.status === "invalid"
                    ? "#dc2626"
                    : state.status === "available"
                    ? "var(--color-brand-primary)"
                    : "var(--color-border-default)",
                background: "var(--color-surface)",
              }}
            >
              <span
                className="font-medium mr-0.5"
                style={{ color: "var(--color-text-secondary)" }}
              >
                @
              </span>
              <input
                type="text"
                value={value}
                onChange={(e) =>
                  setValue(e.target.value.replace(/[^a-zA-Z0-9._]/g, ""))
                }
                placeholder="yourhandle"
                maxLength={20}
                autoFocus
                className="flex-1 bg-transparent outline-none text-sm"
                data-testid="reserve-handle-input"
              />
              <span className="ml-2 flex items-center" aria-hidden="true">
                {state.status === "checking" && (
                  <Loader2 size={16} className="animate-spin text-slate-400" />
                )}
                {state.status === "available" && (
                  <Check size={16} style={{ color: "var(--color-brand-primary)" }} />
                )}
                {(state.status === "taken" || state.status === "invalid") && (
                  <X size={16} className="text-red-600" />
                )}
              </span>
            </div>
            <p
              className="mt-1.5 text-xs min-h-[1rem]"
              style={{
                color:
                  state.status === "available"
                    ? "var(--color-brand-primary)"
                    : state.status === "taken" || state.status === "invalid"
                    ? "#dc2626"
                    : "var(--color-text-secondary)",
              }}
              data-testid="reserve-handle-status"
            >
              {state.status === "available" && `@${value.trim().toLowerCase()} is available`}
              {state.status === "taken" && state.reason}
              {state.status === "invalid" && state.reason}
              {state.status === "checking" && "Checking…"}
              {state.status === "idle" && "3–20 characters. Letters, numbers, dots, underscores."}
            </p>
          </div>

          <button
            onClick={reserveAndSignUp}
            disabled={state.status !== "available"}
            className="w-full rounded-full py-2.5 font-medium text-sm transition-colors"
            style={{
              background:
                state.status === "available"
                  ? "var(--color-brand-primary)"
                  : "var(--color-surface-subtle)",
              color:
                state.status === "available"
                  ? "var(--color-text-on-brand)"
                  : "var(--color-text-secondary)",
              cursor: state.status === "available" ? "pointer" : "not-allowed",
            }}
            data-testid="reserve-handle-submit"
          >
            Reserve this handle & sign up
          </button>

          <p
            className="text-[11px] text-center"
            style={{ color: "var(--color-text-secondary)" }}
          >
            We'll create your account with Google. Your handle will be claimed as soon as you finish.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
